import type { CSSProperties } from "react";
import { SITE_PLAN_LEGEND } from "@/lib/sitePlan";

export interface SitePlanCategoryLegendItem {
  id: string;
  name: string;
  color: string;
}

function CategorySwatches({
  categories,
  compact,
}: {
  categories: SitePlanCategoryLegendItem[];
  compact: boolean;
}) {
  if (categories.length === 0) return null;
  return (
    <ul
      className={
        compact
          ? "flex flex-wrap gap-x-3 gap-y-1.5"
          : "mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-4"
      }
      aria-label="Stellplatzkategorien"
    >
      {categories.map((category) => (
        <li
          key={category.id}
          className={
            compact
              ? "inline-flex min-w-0 items-center gap-1.5 text-[0.6875rem] leading-none text-label-secondary"
              : "flex min-w-0 items-center gap-2.5 text-xs leading-snug text-label-secondary"
          }
        >
          <span
            className="site-plan-legend__swatch"
            style={{ "--pitch-accent": category.color } as CSSProperties}
            aria-hidden
          />
          <span className="min-w-0">{category.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function SitePlanLegend({
  compact = false,
  categories = [],
}: {
  compact?: boolean;
  categories?: SitePlanCategoryLegendItem[];
}) {
  if (compact) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <ul className="flex flex-wrap gap-x-3 gap-y-1.5" aria-label="Belegungsstatus">
          {SITE_PLAN_LEGEND.map((item) => (
            <li
              key={item.label}
              className="inline-flex min-w-0 items-center gap-1.5 text-[0.6875rem] leading-none text-label-secondary"
            >
              <span
                className={`site-plan-legend__swatch site-plan-legend__swatch--${item.accent}`}
                aria-hidden
              />
              <span className="min-w-0">{item.label}</span>
            </li>
          ))}
        </ul>
        <CategorySwatches categories={categories} compact />
      </div>
    );
  }

  return (
    <div className="site-plan-legend">
      <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
        Status
      </p>
      <ul className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-4">
        {SITE_PLAN_LEGEND.map((item) => (
          <li
            key={item.label}
            className="flex min-w-0 items-center gap-2.5 text-xs leading-snug text-label-secondary"
          >
            <span
              className={`site-plan-legend__swatch site-plan-legend__swatch--${item.accent}`}
              aria-hidden
            />
            <span className="min-w-0">{item.label}</span>
          </li>
        ))}
      </ul>
      {categories.length > 0 ? (
        <>
          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-label-tertiary">
            Stellplatzkategorien
          </p>
          <CategorySwatches categories={categories} compact={false} />
        </>
      ) : null}
    </div>
  );
}
